'use client'

import * as React from 'react'
import { useState } from 'react'
import { Toast, ToastProvider, ToastViewport, ToastTitle, ToastDescription } from '@paitox/ui/components/ui/toast'
import { Eye, EyeOff, Loader2, Shield, Mail, Lock } from 'lucide-react'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  const handleBypassLogin = (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    localStorage.setItem('user_session', JSON.stringify({
      id: 'admin',
      email: 'admin@innovaderm.cl',
      name: 'Administrador',
      role: 'admin'
    }))
    showToast('success', '¡Bienvenido a Innovaderm! Redirigiendo...')
    setTimeout(() => {
      window.location.href = '/app/calendar'
    }, 1000)
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden px-4 py-12">
      {/* Background Video with a slight zoom (scale-110) for closer feel */}
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover scale-110 z-0">
        <source src="/bg-video.mp4" type="video/mp4" />
      </video>
      
      {/* Dark Burgundy, Black and Blue gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-tr from-black via-rose-950/60 to-blue-950/50 backdrop-blur-[2px] z-10" />

      <ToastProvider>
        {/* Compact, smaller card (max-w-sm) with turquoise glow border */}
        <div className="w-full max-w-sm bg-black/40 border border-cyan-500/20 rounded-[2rem] p-6 shadow-[0_0_30px_rgba(6,182,212,0.2)] backdrop-blur-xl text-white z-20 transition-all">
          <form onSubmit={handleBypassLogin} className="space-y-5">
            <div className="text-center mb-6">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400">
                <Shield className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-light tracking-widest text-white uppercase">INNOVADERM</h2>
              <p className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mt-1">Estética & Bienestar</p>
              <p className="text-xs text-white/60 mt-2">Haz clic en Iniciar Sesión para continuar</p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold pl-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@innovaderm.cl"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-all"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold pl-1">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-10 bg-white/5 border border-white/10 rounded-xl pl-9 pr-9 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-all"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-cyan-500/50 w-3.5 h-3.5 cursor-pointer"
                />
                <span className="text-[11px] text-white/70">Recordarme</span>
              </label>
              <a href="#" className="text-[11px] text-cyan-400 hover:text-cyan-300 transition-colors">
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-10 bg-gradient-to-r from-cyan-500 via-blue-600 to-rose-900 hover:brightness-110 active:scale-[0.98] text-white text-xs font-semibold rounded-xl shadow-lg shadow-cyan-500/10 flex items-center justify-center transition-all disabled:opacity-50 disabled:pointer-events-none mt-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </form>

          <p className="text-[8px] text-center text-white/30 tracking-widest uppercase mt-6">
            Innovaderm • Gestión de Citas
          </p>
        </div>
        <ToastViewport />
        {toast && (
          <Toast variant={toast.type === 'error' ? 'destructive' : 'success'}>
            <ToastTitle>{toast.type === 'error' ? 'Error' : 'Éxito'}</ToastTitle>
            <ToastDescription>{toast.message}</ToastDescription>
          </Toast>
        )}
      </ToastProvider>
    </div>
  )
}